import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Input,
  Alert,
  Avatar,
  Chip,
  Button,
  Skeleton,
  Table,
  TableHeader,
  TableColumn,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Select,
  SelectItem,
} from '@/components/ui';
import {
  DataTable,
  type Column,
  type Pagination,
} from '@/components/common/table';
import { CampusAutocomplete } from '@/components/common/input/CampusAutocomplete';
import { GroupsAutocomplete } from '@/components/common/input/GroupsAutocomplete';
import { SheepfoldAutocomplete } from '@/components/common/input/SheepfoldAutocomplete';
import { getAvatarColorClass } from '@/shared/lib/avatar';
import { StandardIcon } from '@/components/common/icon';
import { ProfileDrawer2 } from '@/components/people/people2/ProfileDrawer2';
import { useApiWithAuth } from '@/hooks/api';
import { useGroups } from '@/store/people/hooks';
import type { ApiResponse } from '@/shared/types/api';
import { useDebounce } from '@/hooks/debounce';
import { isProbablyPhone, formatPhone } from '@/lib/phone';
import { getInitialsFromFirstLast } from '@/shared/lib/names';
import { Icon } from '@iconify/react';

type People2SearchType = 'name' | 'email' | 'phone';

interface People2Person {
  pco_id: string;
  mhx_id?: string | null;
  first_name: string;
  last_name: string;
  primary_email: string | null;
  primary_phone: string | null;
  primary_campus_id: string | null;
  city: string | null;
  status: string | null;
  membership: string | null;
  mhx_groups?: { laygroup1?: string; laygroup2?: string } | null;
  /** Present when include=group_memberships is requested */
  group_memberships?: Array<{ pco_group_id: string }>;
  /** Present when include=service_team_memberships is requested */
  service_team_memberships?: Array<{ pco_team_id: string }>;
}

interface People2Row {
  id: string;
  data: People2Person;
}

interface CampusItem {
  pco_id: string;
  name: string;
}

/** PCO group from MongoDB /pco/groups endpoint */
interface PcoGroupItem {
  pco_id: string;
  name: string;
}

interface PcoServiceTeamItem {
  pco_id: string;
  name: string;
}

function inferSearchType(search: string): People2SearchType {
  if (search.includes('@')) {
    return 'email';
  }

  if (isProbablyPhone(search)) {
    return 'phone';
  }

  return 'name';
}

function getPersonName(person: People2Person): string {
  const fullName =
    `${person.first_name || ''} ${person.last_name || ''}`.trim();
  if (fullName) {
    return fullName;
  }

  return person.primary_email || person.primary_phone || person.pco_id;
}

/** Status display: small colored dot + uppercase label (icon and text same color) */
function StatusCell({ status }: { status: string | null }) {
  const raw = (status || '').trim().toLowerCase();
  const label = raw ? raw.toUpperCase() : '-';
  let dotClass = 'bg-slate-300';
  let textClass = 'text-slate-500';
  if (raw === 'active') {
    dotClass = 'bg-emerald-400';
    textClass = 'text-emerald-500';
  } else if (raw === 'inactive') {
    dotClass = 'bg-slate-300';
    textClass = 'text-slate-500';
  } else if (raw === 'pending') {
    dotClass = 'bg-amber-400';
    textClass = 'text-amber-500';
  }
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs font-semibold ${textClass}`}
    >
      <span
        className={`h-1.5 w-1.5 shrink-0 rounded-full ${dotClass}`}
        aria-hidden
      />
      {label}
    </span>
  );
}

const STATUS_OPTIONS = [
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
];

interface FilterPeopleModalProps {
  isOpen: boolean;
  onClose: () => void;
  campuses: CampusItem[];
  groups: PcoGroupItem[];
  teams: PcoServiceTeamItem[];
  membershipTypes: string[];
  initialCampusId: string;
  initialStatus: string;
  initialMembershipType: string;
  initialSheepfoldId: string;
  initialGroupId: string;
  initialTeamId: string;
  onApply: (
    campusId: string,
    status: string,
    membershipType: string,
    sheepfoldId: string,
    groupId: string,
    teamId: string,
  ) => void;
}

function FilterPeopleModal({
  isOpen,
  onClose,
  campuses,
  groups,
  teams,
  membershipTypes,
  initialCampusId,
  initialStatus,
  initialMembershipType,
  initialSheepfoldId,
  initialGroupId,
  initialTeamId,
  onApply,
}: FilterPeopleModalProps) {
  const [pendingCampusId, setPendingCampusId] = useState(initialCampusId);
  const [pendingStatus, setPendingStatus] = useState(initialStatus);
  const [pendingMembershipType, setPendingMembershipType] = useState(
    initialMembershipType,
  );
  const [pendingSheepfoldId, setPendingSheepfoldId] =
    useState(initialSheepfoldId);
  const [pendingGroupId, setPendingGroupId] = useState(initialGroupId);
  const [pendingTeamId, setPendingTeamId] = useState(initialTeamId);

  useEffect(() => {
    if (isOpen) {
      setPendingCampusId(initialCampusId);
      setPendingStatus(initialStatus);
      setPendingMembershipType(initialMembershipType);
      setPendingSheepfoldId(initialSheepfoldId);
      setPendingGroupId(initialGroupId);
      setPendingTeamId(initialTeamId);
    }
  }, [
    isOpen,
    initialCampusId,
    initialStatus,
    initialMembershipType,
    initialSheepfoldId,
    initialGroupId,
    initialTeamId,
  ]);

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <ModalContent>
        {(onModalClose) => (
          <>
            <ModalHeader className='flex items-center gap-2'>
              <Icon
                icon='mdi:filter-variant'
                width={20}
                height={20}
                className='text-primary'
              />
              Filter People
            </ModalHeader>
            <ModalBody className='gap-5'>
              <div>
                <p className='mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500'>
                  Campus
                </p>
                <CampusAutocomplete
                  items={campuses.map((c) => ({ id: c.pco_id, name: c.name }))}
                  value={pendingCampusId}
                  onChange={setPendingCampusId}
                  placeholder='All campuses'
                  aria-label='Select campus'
                  portal
                />
              </div>
              <div>
                <p className='mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500'>
                  Status
                </p>
                <div className='flex flex-wrap gap-2'>
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s.key}
                      type='button'
                      onClick={() =>
                        setPendingStatus(pendingStatus === s.key ? '' : s.key)
                      }
                      className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                        pendingStatus === s.key
                          ? 'border-primary bg-primary text-white'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className='mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500'>
                  Membership type
                </p>
                <Select
                  aria-label='Select membership type'
                  placeholder='All membership types'
                  items={membershipTypes}
                  selectedKeys={
                    pendingMembershipType ? [pendingMembershipType] : []
                  }
                  onSelectionChange={(keys) =>
                    setPendingMembershipType(keys.currentKey || '')
                  }
                >
                  {(membershipType) => (
                    <SelectItem key={membershipType}>
                      {membershipType}
                    </SelectItem>
                  )}
                </Select>
              </div>
              <div>
                <p className='mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500'>
                  Sheepfold
                </p>
                <SheepfoldAutocomplete
                  value={pendingSheepfoldId}
                  onChange={setPendingSheepfoldId}
                  placeholder='All sections and groups'
                  aria-label='Select sheepfold section or group'
                />
              </div>
              <div>
                <p className='mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500'>
                  Group
                </p>
                <GroupsAutocomplete
                  items={groups.map((g) => ({ id: g.pco_id, name: g.name }))}
                  value={pendingGroupId}
                  onChange={setPendingGroupId}
                  placeholder='All groups'
                  aria-label='Select group'
                  portal
                />
              </div>
              <div>
                <p className='mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500'>
                  Team
                </p>
                <GroupsAutocomplete
                  items={teams.map((team) => ({
                    id: team.pco_id,
                    name: team.name,
                  }))}
                  value={pendingTeamId}
                  onChange={setPendingTeamId}
                  placeholder='All teams'
                  aria-label='Select team'
                  portal
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant='flat' onPress={onModalClose}>
                Cancel
              </Button>
              <Button
                color='primary'
                onPress={() => {
                  onApply(
                    pendingCampusId,
                    pendingStatus,
                    pendingMembershipType,
                    pendingSheepfoldId,
                    pendingGroupId,
                    pendingTeamId,
                  );
                  onModalClose();
                }}
              >
                Apply Filters
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}

const DISPLAY_PAGE_SIZE = 20;
const LOAD_PAGE_SIZE = 100;
const CAMPUSES_FETCH_LIMIT = 500;

const PEOPLE2_COLUMNS: { key: string; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'campus', label: 'Campus' },
  { key: 'city', label: 'City' },
  { key: 'status', label: 'Status' },
  { key: 'membership', label: 'Membership' },
  { key: 'sheepfold', label: 'Sheepfold' },
  { key: 'groups', label: 'Groups' },
  { key: 'teams', label: 'Teams' },
  { key: 'actions', label: '' },
];

function People2TableSkeleton({
  columns,
  rowCount = DISPLAY_PAGE_SIZE,
}: {
  columns: typeof PEOPLE2_COLUMNS;
  rowCount?: number;
}) {
  return (
    <Table
      isHeaderSticky
      className='max-h-[calc(100vh-220px)] text-sm text-gray-800'
    >
      <TableHeader columns={columns}>
        {(col) => <TableColumn key={col.key}>{col.label}</TableColumn>}
      </TableHeader>
      <tbody className='divide-y divide-slate-100'>
        {Array.from({ length: rowCount }).map((_, i) => (
          <tr key={i}>
            <td className='px-3 py-2 align-middle'>
              <div className='flex items-center gap-3'>
                <Skeleton className='h-8 w-8 shrink-0 rounded-full' />
                <Skeleton className='h-4 w-32 rounded-md' />
              </div>
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-4 w-40 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-4 w-28 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-6 w-24 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-4 w-20 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-4 w-16 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-6 w-28 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-10 w-24 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-10 w-24 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-10 w-24 rounded-md' />
            </td>
            <td className='px-3 py-2 align-middle'>
              <Skeleton className='h-8 w-8 rounded-md' />
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

export function People2() {
  const { invoke, auth } = useApiWithAuth();
  const invokeRef = useRef(invoke);
  invokeRef.current = invoke;

  const [campuses, setCampuses] = useState<CampusItem[]>([]);
  const [membershipTypes, setMembershipTypes] = useState<string[]>([]);
  const [pcoGroups, setPcoGroups] = useState<PcoGroupItem[]>([]);
  const [pcoServiceTeams, setPcoServiceTeams] = useState<PcoServiceTeamItem[]>(
    [],
  );
  const [chunks, setChunks] = useState<Record<number, People2Row[]>>({});
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchInput = useDebounce(searchInput, 350);
  const [page, setPage] = useState(1);
  const [displayPageSize, setDisplayPageSize] = useState(DISPLAY_PAGE_SIZE);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterCampusId, setFilterCampusId] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [filterMembershipType, setFilterMembershipType] = useState('');
  const [filterSheepfoldId, setFilterSheepfoldId] = useState('');
  const [filterGroupId, setFilterGroupId] = useState('');
  const [filterTeamId, setFilterTeamId] = useState('');
  const [profileModalPcoId, setProfileModalPcoId] = useState<string | null>(
    null,
  );

  const { lookup: groupLookup } = useGroups(undefined, undefined, undefined);

  const campusLookup = useMemo(
    () =>
      campuses.reduce<Record<string, string>>((acc, c) => {
        acc[c.pco_id] = c.name;

        return acc;
      }, {}),
    [campuses],
  );

  const pcoGroupNameLookup = useMemo(
    () =>
      pcoGroups.reduce<Record<string, string>>((acc, g) => {
        acc[g.pco_id] = g.name;

        return acc;
      }, {}),
    [pcoGroups],
  );

  const pcoServiceTeamNameLookup = useMemo(
    () =>
      pcoServiceTeams.reduce<Record<string, string>>((acc, team) => {
        acc[team.pco_id] = team.name;

        return acc;
      }, {}),
    [pcoServiceTeams],
  );

  const columns = useMemo(
    (): Column<People2Row>[] => [
      {
        key: 'name',
        label: 'Name',
        display: ({ item }) => {
          const name = getPersonName(item.data);
          const initials =
            getInitialsFromFirstLast(
              item.data.first_name,
              item.data.last_name,
            ) || item.data.pco_id.slice(0, 2).toUpperCase();
          return (
            <div className='flex items-center gap-3'>
              <Avatar
                name={name}
                getInitials={() => initials}
                className={getAvatarColorClass(name)}
                size='sm'
              />
              <span className='font-medium text-slate-800'>{name}</span>
            </div>
          );
        },
      },
      {
        key: 'email',
        label: 'Email',
        display: ({ item }) => (
          <span className='text-slate-700'>
            {item.data.primary_email || '-'}
          </span>
        ),
      },
      {
        key: 'phone',
        label: 'Phone',
        display: ({ item }) => (
          <span className='text-slate-700'>
            {formatPhone(item.data.primary_phone)}
          </span>
        ),
      },
      {
        key: 'campus',
        label: 'Campus',
        display: ({ item }) => {
          const campusId = item.data.primary_campus_id;
          const label = campusId
            ? (campusLookup[String(campusId)] ?? campusId)
            : null;
          if (label === null || label === undefined) {
            return <></>;
          }
          return (
            <Chip className='rounded-md border border-slate-200 bg-slate-100 px-3 py-1.5 text-xs text-slate-700'>
              {label}
            </Chip>
          );
        },
      },
      {
        key: 'city',
        label: 'City',
        display: ({ item }) => (
          <span className='text-slate-700'>{item.data.city ?? '-'}</span>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        display: ({ item }) => <StatusCell status={item.data.status} />,
      },
      {
        key: 'membership',
        label: 'Membership',
        display: ({ item }) =>
          item.data.membership ? (
            <Chip className='rounded-md border border-slate-200 bg-slate-100 px-2.5 py-1 text-xs text-slate-700'>
              {item.data.membership}
            </Chip>
          ) : (
            <span className='text-slate-400'>—</span>
          ),
      },
      {
        key: 'sheepfold',
        label: 'Sheepfold',
        display: ({ item }) => {
          const mg = item.data.mhx_groups;
          const sectionId = mg?.laygroup1;
          const groupId = mg?.laygroup2;
          const sectionTitle = sectionId
            ? (groupLookup[sectionId]?.data?.title ?? null)
            : null;
          const groupTitle = groupId
            ? (groupLookup[groupId]?.data?.title ?? null)
            : null;
          if (!sectionTitle && !groupTitle) {
            return <span className='text-slate-400'>—</span>;
          }
          return (
            <div className='flex flex-col'>
              {groupTitle ? (
                <span className='font-medium text-slate-800'>{groupTitle}</span>
              ) : null}
              {sectionTitle ? (
                <span className='text-xs text-slate-500'>{sectionTitle}</span>
              ) : null}
            </div>
          );
        },
      },
      {
        key: 'groups',
        label: 'Groups',
        display: ({ item }) => {
          const memberships = item.data.group_memberships ?? [];
          if (memberships.length === 0) {
            return <span className='text-slate-400'>—</span>;
          }
          return (
            <div className='flex flex-wrap gap-1.5'>
              {memberships.map((m) => {
                const name =
                  pcoGroupNameLookup[m.pco_group_id] ?? m.pco_group_id;
                return (
                  <Chip
                    key={m.pco_group_id}
                    className='rounded-md border border-slate-200 bg-slate-100 px-2.5 py-1 text-xs text-slate-700'
                  >
                    {name}
                  </Chip>
                );
              })}
            </div>
          );
        },
      },
      {
        key: 'teams',
        label: 'Teams',
        display: ({ item }) => {
          const memberships = item.data.service_team_memberships ?? [];
          if (memberships.length === 0) {
            return <span className='text-slate-400'>—</span>;
          }

          return (
            <div className='flex flex-wrap gap-1.5'>
              {memberships.map((membership) => {
                const name =
                  pcoServiceTeamNameLookup[membership.pco_team_id] ??
                  membership.pco_team_id;
                return (
                  <Chip
                    key={membership.pco_team_id}
                    className='rounded-md border border-slate-200 bg-slate-100 px-2.5 py-1 text-xs text-slate-700'
                  >
                    {name}
                  </Chip>
                );
              })}
            </div>
          );
        },
      },
      {
        key: 'actions',
        label: '',
        display: ({ item }) => (
          <Button
            isIconOnly
            variant='light'
            size='sm'
            className='w-8 min-w-8 text-slate-500'
            aria-label={`Open ${getPersonName(item.data)}`}
            onPress={() => {
              setProfileModalPcoId(item.data.pco_id);
            }}
          >
            <Icon icon='mdi:open-in-new' width={20} height={20} />
          </Button>
        ),
      },
    ],
    [campusLookup, groupLookup, pcoGroupNameLookup, pcoServiceTeamNameLookup],
  );

  useEffect(() => {
    if (!auth.ready) {
      return undefined;
    }

    let cancelled = false;

    const loadCampuses = async () => {
      try {
        const params = new URLSearchParams();
        params.set('limit', String(CAMPUSES_FETCH_LIMIT));
        params.set('offset', '0');
        const response = (await invokeRef.current(
          `/pco-campuses?${params.toString()}`,
        )) as ApiResponse<CampusItem[]>;
        if (cancelled) {
          return;
        }
        const list =
          response?.data && Array.isArray(response.data)
            ? response.data.map((c: CampusItem) => ({
                pco_id: c.pco_id,
                name: c.name,
              }))
            : [];
        setCampuses(list);
      } catch (err) {
        if (cancelled) {
          return;
        }
        console.error('Failed loading campuses for People 2:', err);
        setCampuses([]);
      }
    };

    loadCampuses();

    return () => {
      cancelled = true;
    };
  }, [auth.ready]);

  useEffect(() => {
    if (!auth.ready) {
      return undefined;
    }

    let cancelled = false;

    const loadMembershipTypes = async () => {
      try {
        const response = (await invokeRef.current(
          '/people2/membership-types',
        )) as ApiResponse<string[]>;
        if (cancelled) {
          return;
        }

        setMembershipTypes(
          Array.isArray(response?.data)
            ? response.data.filter(
                (value): value is string =>
                  typeof value === 'string' && Boolean(value.trim()),
              )
            : [],
        );
      } catch (err) {
        if (cancelled) {
          return;
        }

        console.error('Failed loading PCO membership types:', err);
        setMembershipTypes([]);
      }
    };

    loadMembershipTypes();

    return () => {
      cancelled = true;
    };
  }, [auth.ready]);

  useEffect(() => {
    if (!auth.ready) {
      return undefined;
    }

    let cancelled = false;

    const loadPcoGroups = async () => {
      try {
        const params = new URLSearchParams();
        params.set('limit', '1000');
        params.set('offset', '0');
        const response = (await invokeRef.current(
          `/pco/groups?${params.toString()}`,
        )) as ApiResponse<PcoGroupItem[]>;
        if (cancelled) {
          return;
        }
        const list =
          response?.data && Array.isArray(response.data)
            ? response.data.map((g: PcoGroupItem) => ({
                pco_id: g.pco_id,
                name: g.name,
              }))
            : [];
        setPcoGroups(list);
      } catch (err) {
        if (cancelled) {
          return;
        }
        console.error('Failed loading PCO groups for People 2:', err);
        setPcoGroups([]);
      }
    };

    loadPcoGroups();

    return () => {
      cancelled = true;
    };
  }, [auth.ready]);

  useEffect(() => {
    if (!auth.ready) {
      return undefined;
    }

    let cancelled = false;

    const loadPcoServiceTeams = async () => {
      try {
        const params = new URLSearchParams();
        params.set('limit', '1000');
        params.set('offset', '0');
        const response = (await invokeRef.current(
          `/pco/services/teams?${params.toString()}`,
        )) as ApiResponse<PcoServiceTeamItem[]>;
        if (cancelled) {
          return;
        }

        const list =
          response?.data && Array.isArray(response.data)
            ? response.data.map((team: PcoServiceTeamItem) => ({
                pco_id: team.pco_id,
                name: team.name,
              }))
            : [];
        setPcoServiceTeams(list);
      } catch (err) {
        if (cancelled) {
          return;
        }

        console.error('Failed loading PCO Services teams for People 2:', err);
        setPcoServiceTeams([]);
      }
    };

    loadPcoServiceTeams();

    return () => {
      cancelled = true;
    };
  }, [auth.ready]);

  const search = debouncedSearchInput.trim();
  const searchType = search ? inferSearchType(search) : '';

  const startIndex = (page - 1) * displayPageSize;
  const chunkIndex = Math.floor(startIndex / LOAD_PAGE_SIZE);
  const startInChunk = startIndex % LOAD_PAGE_SIZE;

  const currentChunk = chunks[chunkIndex];
  const displayedItems = useMemo(() => {
    if (!currentChunk) {
      return [];
    }

    return currentChunk.slice(startInChunk, startInChunk + displayPageSize);
  }, [currentChunk, startInChunk, displayPageSize]);

  const hasChunkForPage = currentChunk !== undefined;

  useEffect(() => {
    if (hasChunkForPage) {
      return () => {};
    }

    const offset = chunkIndex * LOAD_PAGE_SIZE;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const params = new URLSearchParams();
        params.set('limit', String(LOAD_PAGE_SIZE));
        params.set('offset', String(offset));
        if (filterCampusId) {
          params.set('campusId', filterCampusId);
        }
        if (filterStatus) {
          params.set('status', filterStatus);
        }
        if (filterMembershipType) {
          params.set('membershipType', filterMembershipType);
        }
        if (filterSheepfoldId) {
          params.set('sheepfoldId', filterSheepfoldId);
        }
        if (filterGroupId) {
          params.set('groupId', filterGroupId);
        }
        if (filterTeamId) {
          params.set('teamId', filterTeamId);
        }
        params.set('include', 'group_memberships,service_team_memberships');
        if (search) {
          params.set('search', search);
          params.set('searchType', searchType);
        } else if (chunkIndex === 0) {
          params.set('count', 'true');
        }

        const response = (await invokeRef.current(
          `/people2?${params.toString()}`,
        )) as ApiResponse<People2Person[]> & { count?: number };
        if (cancelled) {
          return;
        }

        const rows = (response.data || []).map((person: People2Person) => ({
          id: person.pco_id,
          data: person,
        }));
        setChunks((prev) => ({ ...prev, [chunkIndex]: rows }));
        if (response.count !== undefined) {
          setTotalCount(response.count);
        } else if (search) {
          setTotalCount(null);
        }
      } catch (err) {
        if (cancelled) {
          return;
        }

        console.error('Failed loading People 2:', err);
        setChunks((prev) => {
          const next = { ...prev };
          delete next[chunkIndex];

          return next;
        });
        setError('Unable to load People 2 data');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [
    auth.firebaseUser?.uid,
    debouncedSearchInput,
    chunkIndex,
    hasChunkForPage,
    search,
    searchType,
    filterCampusId,
    filterStatus,
    filterMembershipType,
    filterSheepfoldId,
    filterGroupId,
    filterTeamId,
  ]);

  useEffect(() => {
    setPage(1);
    setChunks({});
    setTotalCount(null);
  }, [
    debouncedSearchInput,
    filterCampusId,
    filterStatus,
    filterMembershipType,
    filterSheepfoldId,
    filterGroupId,
    filterTeamId,
  ]);

  const pagination: Pagination = useMemo(
    () => ({
      enabled: true,
      absolute: true,
      page,
      itemsPerPage: displayPageSize,
      allowItemsPerPage: [10, 20, 50, 100],
      onPageChange: (nextPage: number) => setPage(nextPage),
      onItemsPerPageChange: (value: number) => {
        setDisplayPageSize(value);
        setPage(1);
      },
    }),
    [page, displayPageSize],
  );

  const hasActiveFilters = Boolean(
    filterCampusId ||
    filterStatus ||
    filterMembershipType ||
    filterSheepfoldId ||
    filterGroupId ||
    filterTeamId,
  );

  return (
    <div>
      <div className='mb-2 flex flex-col gap-2 sm:flex-row sm:items-center'>
        <Input
          className='basis-1/2'
          name='people-search'
          value={searchInput}
          onValueChange={setSearchInput}
          isClearable
          onClear={() => setSearchInput('')}
          placeholder='Search by name, email or phone'
          startContent={<StandardIcon id='mdi:search' />}
          autoComplete='off'
        />
        <Button
          variant='flat'
          className={`relative border border-slate-200 bg-white text-slate-700 ${hasActiveFilters ? 'border-primary/50 text-primary' : ''}`}
          startContent={
            <Icon icon='mdi:filter-variant' width={18} height={18} />
          }
          onPress={() => setIsFilterOpen(true)}
        >
          Filter
          {hasActiveFilters && (
            <span className='absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-primary' />
          )}
        </Button>
      </div>
      <FilterPeopleModal
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        campuses={campuses}
        groups={pcoGroups}
        teams={pcoServiceTeams}
        membershipTypes={membershipTypes}
        initialCampusId={filterCampusId}
        initialStatus={filterStatus}
        initialMembershipType={filterMembershipType}
        initialSheepfoldId={filterSheepfoldId}
        initialGroupId={filterGroupId}
        initialTeamId={filterTeamId}
        onApply={(
          campusId,
          status,
          membershipType,
          sheepfoldId,
          groupId,
          teamId,
        ) => {
          setFilterCampusId(campusId);
          setFilterStatus(status);
          setFilterMembershipType(membershipType);
          setFilterSheepfoldId(sheepfoldId);
          setFilterGroupId(groupId);
          setFilterTeamId(teamId);
        }}
      />
      <ProfileDrawer2
        isOpen={profileModalPcoId !== null}
        onClose={() => setProfileModalPcoId(null)}
        pcoId={profileModalPcoId}
        campusLookup={campusLookup}
        onOpenProfile={(nextPcoId) => setProfileModalPcoId(nextPcoId)}
      />
      {error ? (
        <Alert color='danger' className='mb-2'>
          {error}
        </Alert>
      ) : null}
      {loading && !hasChunkForPage ? (
        <People2TableSkeleton
          columns={PEOPLE2_COLUMNS}
          rowCount={displayPageSize}
        />
      ) : (
        <DataTable
          aria-label='People 2'
          columns={columns}
          items={displayedItems}
          count={
            (totalCount ??
              Object.values(chunks).reduce((sum, c) => sum + c.length, 0)) ||
            displayedItems.length
          }
          pagination={pagination}
          paginationVariant='numbered'
          loading={loading && !hasChunkForPage}
          className='text-sm text-gray-800'
          empty={{
            icon: 'mdi:account-search',
            title: 'No people found',
            body: "We couldn't find anyone matching your current selection. Try adjusting your filters or adding a new person to your organization.",
            actions: (
              <>
                <Button
                  color='primary'
                  startContent={
                    <Icon icon='mdi:account-plus' width={18} height={18} />
                  }
                >
                  Add Person
                </Button>
                <Button variant='flat' onPress={() => setSearchInput('')}>
                  Clear Filters
                </Button>
              </>
            ),
          }}
        />
      )}
    </div>
  );
}
